import React, { useEffect, useState } from 'react';
import { Icons } from '@/utils/Icons';
import { useLockBodyScroll } from '@/hooks';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { useGetBetSummaryQuery } from '@/store/api/betApi';

interface Props {
  open: boolean;
  onClose: () => void;
}

const MyMarketModal: React.FC<Props> = ({ open, onClose }) => {
  useLockBodyScroll(open);

  const [page, setPage] = useState<number>(1);
  const limit = 10;

  // ✅ Skip API call when modal is closed
  const { data, isFetching } = useGetBetSummaryQuery({ page, limit }, { skip: !open });

  const tableData = data?.data?.result ?? [];

  // ✅ Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setPage(1);
    }
  }, [open]);

  const modalRef = useOutsideClick<HTMLDivElement>(() => {
    if (open) onClose();
  }, open);

  if (!open) return null;

  const handleNext = () => {
    if (tableData.length === limit) {
      setPage((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (page > 1) {
      setPage((prev) => prev - 1);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center md:p-4 md:pt-[2vh]">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative z-10 w-full max-w-[900px] bg-white shadow-xl rounded-md max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-black text-white px-4 py-3 flex justify-between items-center shrink-0">
          <h2 className="text-lg font-semibold">My Market</h2>
          <button onClick={onClose}>
            <Icons.Cross className="size-6 text-white" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto md:p-2">
          <div>
            <table className="w-full text-xs md:text-base border-collapse">
              <thead className="sticky top-0 bg-gray-100 z-10">
                <tr className="text-black text-left">
                  <th className="px-1 md:px-3 py-2">Event Type</th>
                  <th className="px-1 md:px-3 py-2">Event Name</th>
                  <th className="px-1 md:px-3 py-2">Match Name</th>
                  <th className="px-1 md:px-3 py-2 text-center">Trade</th>
                </tr>
              </thead>

              <tbody>
                {isFetching ? (
                  <tr>
                    <td colSpan={4} className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                ) : tableData.length > 0 ? (
                  tableData.map((item, index) => (
                    <tr
                      key={index}
                      className="border-y border-neutral-300 bg-neutral-100 hover:bg-gray-100 transition"
                    >
                      <td className="px-1 md:px-3 py-2">{item.sport}</td>

                      <td className="px-1 md:px-3 py-2 text-blue-600 cursor-pointer hover:underline">
                        {item.eventName}
                      </td>

                      <td className="px-1 md:px-3 py-2">{item.marketName}</td>

                      <td className="px-1 md:px-3 py-2 text-center">{item.betCount}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4} className="text-center py-4 text-gray-500">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer / Pagination */}
        <div className="flex justify-between items-center px-4 py-2 border-t bg-white shrink-0">
          <button
            onClick={handlePrev}
            disabled={page === 1}
            className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
          >
            Prev
          </button>

          <span className="text-sm font-medium">Page {page}</span>

          <button
            onClick={handleNext}
            disabled={tableData.length < limit}
            className="px-3 py-1 bg-gray-300 rounded disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default MyMarketModal;
